import { autoinject, transient, useView } from "aurelia-framework";
import { MepUpdateLongTermYearConfigRequest } from "../../../../../../models/mep/mep-emission-target-config.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { MepEmissionTargetConfigService } from "../../../../../../services/mep/mep-emission-target-config.service";
import * as Enumerable from "linq";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { BindingSignaler } from "aurelia-templating-resources";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { MepPortDetails } from "../../../../../../models/mep/mep.models";
import { MepService } from "../../../../../../services/mep/mep.service";
import { MepEmissionTypesConfig, MepEmissionTypesConfigVesselGroupViewModel } from "./partials/mep-emission-types-config/mep-emission-types-config";
import { MepEmissionTargetsFilters } from "./partials/mep-emission-targets-filters/mep-emission-targets-filters";

@autoinject
@transient()
@useView('views/pages/modules/mep/tabs/mep-emission-targets/mep-emission-targets.html')
export class MepEmissionTargets {

    //this is so that the VM is always created new 
    determineActivationStrategy() {
        return "replace";
    }

    public portDetails: MepPortDetails;
    portId: number;
    isSetAbsoluteTarget: boolean = true;
    targets: MepEmissionTypesConfig[] = null;
    inActiveTargets: MepEmissionTypesConfig[] = null;
    loadingTargets: boolean = false;
    savingLongTermYear: boolean = false;
    filterViewModel: MepEmissionTargetsFilters;
    onRemoveChild: () => void;

    //Claims
    isReadOnly: boolean = true;

    constructor(protected dialogClientService: DialogClientService, private mepEmissionTargetConfigService: MepEmissionTargetConfigService,
        private notificationClientService: NotificationClientService, userService: UserClaimsService, private bindingSignaler: BindingSignaler,
        private mepService: MepService) {

        this.isReadOnly = !userService.hasPermission(Claims.CanCreateOrUpdateEmissionTargets);

        this.onRemoveChild = () => this.activateConfigItems();
    }

    activate(param) {
        this.portId = param.portId;
        this.filterViewModel = new MepEmissionTargetsFilters(this.mepEmissionTargetConfigService, this.notificationClientService, param.portId, this.onSelectedYearChanged);
    }

    async attached() {
        
        this.loadPortDetails();
    }

    loadPortDetails()
    {
        this.mepService.getMepPortDetails(this.portId, true).then(x => {
            this.portDetails = x;
        }).catch(() => {
            this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
        });
    }

    onSelectedYearChanged = () => {
        if (this.portId && this.filterViewModel && this.filterViewModel.selectedYear) {
            this.loadEmissionTargets();
        }
    }

    loadEmissionTargets() {
        if (this.filterViewModel.selectedYear) {
            this.loadingTargets = true;
            this.targets = null;

            this.mepEmissionTargetConfigService.getPortEmissionTypesConfig(this.portId, this.filterViewModel.selectedYear.year)
                .then(result => {

                    this.loadingTargets = false;

                    if (result.hasError) {
                        this.notificationClientService.show("MEP Targets", "Error retrieving the emission targets!", NotificationType.Error);
                    }
                    else {

                        const allResults = Enumerable.from(result.emissionTypeConfigs).select(x => {
                            return new MepEmissionTypesConfig(x, this.portId, this.filterViewModel.selectedYear, this.filterViewModel.baseYears,
                                this.mepEmissionTargetConfigService, this.notificationClientService, this.bindingSignaler, this.onRemoveChild);
                        });

                        this.inActiveTargets = allResults.where(x => !x.isActive).toArray();
                        this.targets = allResults.where(x => x.isActive).toArray();
                    }

                }).catch(() => {
                    this.loadingTargets = false;
                    this.notificationClientService.show("MEP Targets", "Error retrieving the emission targets!", NotificationType.Error);
                });
        } else {
            this.notificationClientService.show("MEP Targets", "Year not selected", NotificationType.Error);
        }
    }

    //called from the dialog 
    activateConfigItems() {
        const oldActiveTargerts = this.targets;
        const allResults = Enumerable.from(this.targets).concat(this.inActiveTargets);
        this.inActiveTargets = allResults.where(x => !x.isActive).toArray();
        this.targets = allResults.where(x => x.isActive).toArray();

        this.targets.forEach(target => {
            if (target && oldActiveTargerts.indexOf(target) < 0) {
                target.loadDetails().then(() => {
                    target.save();
                });
            }
        });
    }

    openTargetDetails(detail: MepEmissionTypesConfigVesselGroupViewModel) {
        detail.isOpen = !detail.isOpen;
    }

    addANewEmissionType() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddEmissionTypeDialog, this, true, false, false, "right");
    }

    updateLongTermYear() {
        const newLongTermYear = Number(this.filterViewModel.longTermYear);
        let errorMessage = null;
        if (newLongTermYear < new Date().getFullYear()) {
            errorMessage = "Cannot select a past year for Long term year!";
        }
        if (newLongTermYear > 3000) {
            errorMessage = "Cannot select a  year greater than 3000 for Long term year!";
        }

        if (errorMessage) {
            this.notificationClientService.show("Emission targets", errorMessage, NotificationType.Error);
        } else {

            this.savingLongTermYear = true;
            const requestData: MepUpdateLongTermYearConfigRequest = {
                portId: this.portId,
                year: newLongTermYear
            };
            this.mepEmissionTargetConfigService.updateLongTermYearConfig(requestData)
                .then(x => {
                    this.savingLongTermYear = false;
                    if (x.hasError) {
                        this.notificationClientService.show("MEP Targets", "Error saving long term year!", NotificationType.Error);
                    } else {
                        this.notificationClientService.show("MEP Targets", "Long Term year saved!", NotificationType.Info);
                        this.filterViewModel.initYears(); // refresh years 
                    }
                }).catch(() => {
                    this.savingLongTermYear = false;
                    this.notificationClientService.show("MEP Targets", "Error saving long term year!", NotificationType.Error);
                });
        }
    }

    refresh(data?: {portId: number})
    {
        if(this.portDetails)
        {
            this.portId = data.portId;
            this.loadPortDetails();
            if(this.filterViewModel)
            {
                this.inActiveTargets = null;
                this.targets = null;
                this.filterViewModel.portId = data.portId;
                this.filterViewModel.initYears();
            }
        }
    }
}